import {
  CommonModule,
  Location,
} from '@angular/common';

import {
  Component,
  OnInit,
  computed,
  signal,
} from '@angular/core';

import {
  ActivatedRoute,
  RouterLink,
} from '@angular/router';

import { LoaderService } from '@app-shared/services/loader.service';

import {
  FontAwesomeModule,
} from '@fortawesome/angular-fontawesome';

import {
  faAngleLeft,
} from '@fortawesome/free-solid-svg-icons';

import { AlertService } from '@main/alert.service';
import { BatchCloningService } from '@main/batch-cloning.service';

import {
  NgbDropdownModule,
  NgbPaginationModule,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';

type CloneStepStatus =
  | 'success'
  | 'failed'
  | 'errored'
  | 'pending'
  | string;

type ResultStatusFilter =
  | 'all'
  | 'success'
  | 'pending'
  | 'failed'
  | 'errored';

type CloneStep = {
  step: string;
  status: CloneStepStatus;

  info?: {
    message?: string;
  };
};

type CloneResult = {
  sourceProjectId: string;
  sourceProjectTitle: string;

  clientId: string;
  clientName: string;

  newProjectId: string;
  newProjectName: string;
  newProjectYear?: number | null;

  newProposalId?: string | null;
  newProposalName?: string;

  newInvoiceId?: string | null;

  status: string;

  steps: CloneStep[];
};

type CloneJobDetails = {
  jobId: string;
  status: string;

  createdAt: string;
  completedAt?: string;

  createdByName?: string;

  totalItems: number;
  successCount: number;
  failedCount: number;

  results: CloneResult[];
};

@Component({
  selector: 'app-clone-history-details',
  standalone: true,

  imports: [
    CommonModule,
    RouterLink,

    FontAwesomeModule,

    NgbTooltipModule,
    NgbDropdownModule,
    NgbPaginationModule,
  ],

  templateUrl: './clone-history-details.component.html',
  styleUrls: ['./clone-history-details.component.css'],
})
export class CloneHistoryDetailsComponent implements OnInit {
  readonly faAngleLeft = faAngleLeft;

  readonly job = signal<CloneJobDetails | null>(null);

  readonly searchValue = signal('');

  readonly statusFilter =
    signal<ResultStatusFilter>('all');

  readonly page = signal(1);

  readonly limit = signal(10);

  jobId = '';

  /**
   * Results after search + filter.
   */
  readonly filteredResults = computed(() => {
    const rows = this.job()?.results ?? [];

    const search = this.searchValue()
      .trim()
      .toLowerCase();

    const status = this.statusFilter();

    return rows.filter((row) => {
      const matchesSearch =
        !search ||
        this.matchesSearch(row, search);

      const matchesStatus =
        status === 'all' ||
        row.status?.toLowerCase() === status;

      return matchesSearch && matchesStatus;
    });
  });

  /**
   * Total count AFTER filters/search.
   */
  readonly totalCount = computed(() => {
    return this.filteredResults().length;
  });

  /**
   * Rows displayed on the current page.
   */
  readonly viewRows = computed(() => {
    const rows = this.filteredResults();

    const start =
      (this.page() - 1) * this.limit();

    const end =
      start + this.limit();

    return rows.slice(start, end);
  });

  /**
   * Example:
   * Page 1 / limit 10 = 1
   * Page 2 / limit 10 = 11
   */
  readonly displayedRangeStart = computed(() => {
    if (this.totalCount() === 0) {
      return 0;
    }

    return (
      (this.page() - 1) *
        this.limit() +
      1
    );
  });

  /**
   * Prevents something like:
   * Showing 1 to 10 of 7
   */
  readonly displayedRangeEnd = computed(() => {
    return Math.min(
      this.page() * this.limit(),
      this.totalCount(),
    );
  });

  readonly hasActiveFilters = computed(() => {
    return (
      !!this.searchValue() ||
      this.statusFilter() !== 'all'
    );
  });

  constructor(
    public location: Location,
    private route: ActivatedRoute,
    private loaderService: LoaderService,
    private batchCloningService: BatchCloningService,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.jobId =
      this.route.snapshot.paramMap.get('jobId') ??
      '';

    if (!this.jobId) {
      this.alertService.showErrorAlert(
        'Missing cloning job ID.',
      );

      this.location.back();

      return;
    }

    this.loadJobDetails(this.jobId);
  }

  loadJobDetails(jobId: string): void {
    this.loaderService.showSpinner();

    this.batchCloningService
      .getCloningProjectsByJobId(jobId)
      .subscribe({
        next: (res) => {
          this.loaderService.hideSpinner();

          const mappedJob =
            this.mapJobDetails(res);

          this.job.set(mappedJob);

          // Always start table from page 1
          // when loading/reloading.
          this.page.set(1);
        },

        error: (error) => {
          this.loaderService.hideSpinner();

          this.alertService.showErrorAlert(
            error?.error?.message ||
              'Failed to load cloning job details.',
          );
        },
      });
  }

  /**
   * Map the whole API response.
   *
   * API currently places clientId inside items[]
   * while project result information is in results[].
   *
   * We match:
   *
   * items[].projectId
   *     ===
   * results[].sourceProjectId
   */
  private mapJobDetails(
    res: any,
  ): CloneJobDetails {
    const items = Array.isArray(res?.items)
      ? res.items
      : [];

    const results = Array.isArray(res?.results)
      ? res.results.map((result: any) => {
          const matchingItem =
            items.find(
              (item: any) =>
                item?.projectId ===
                result?.sourceProjectId,
            );

          return this.mapCloneResult(
            result,
            matchingItem,
          );
        })
      : [];

    return {
      jobId: res?.jobId ?? '',

      status:
        this.normalizeJobStatus(
          res?.status,
        ),

      createdAt:
        res?.createdAt ?? '',

      completedAt:
        res?.completedAt ?? '',

      createdByName:
        res?.createdByName ?? '',

      totalItems:
        Number(res?.totalItems ?? 0),

      successCount:
        Number(res?.successCount ?? 0),

      failedCount:
        Number(res?.failedCount ?? 0),

      results,
    };
  }

  private mapCloneResult(
    result: any,
    item?: any,
  ): CloneResult {
    return {
      sourceProjectId:
        result?.sourceProjectId ??
        item?.projectId ??
        '',

      sourceProjectTitle:
        result?.sourceProjectTitle ??
        '',

      clientId:
        result?.clientId ??
        item?.clientId ??
        '',

      clientName:
        result?.clientName ??
        item?.clientName ??
        '',

      newProjectId:
        result?.newProjectId ??
        '',

      newProjectName:
        result?.newProjectName ??
        item?.newProjectName ??
        '',

      newProjectYear:
        item?.newProjectYear ??
        null,

      newProposalId:
        result?.newProposalId ??
        null,

      newProposalName:
        result?.newProposalName ??
        '',

      newInvoiceId:
        result?.newInvoiceId ??
        null,

      status:
        this.normalizeStepStatus(
          result?.status,
        ),

      steps:
        Array.isArray(result?.steps)
          ? result.steps.map(
              (step: any): CloneStep => ({
                step:
                  step?.step ?? '',

                status:
                  this.normalizeStepStatus(
                    step?.status,
                  ),

                info:
                  step?.info,
              }),
            )
          : [],
    };
  }

  /**
   * Search
   */
  search(value: string): void {
    this.searchValue.set(
      value.trim(),
    );

    this.page.set(1);
  }

  clearSearch(
    input: HTMLInputElement,
  ): void {
    input.value = '';

    this.searchValue.set('');

    this.page.set(1);
  }

  /**
   * Status filter
   */
  setStatusFilter(
    status: ResultStatusFilter,
  ): void {
    this.statusFilter.set(status);

    this.page.set(1);
  }

  clearFilters(): void {
    this.statusFilter.set('all');

    this.page.set(1);
  }

  clearAllFilters(
    searchInput: HTMLInputElement,
  ): void {
    searchInput.value = '';

    this.searchValue.set('');

    this.statusFilter.set('all');

    this.page.set(1);
  }

  /**
   * Pagination
   */
  setLimit(limit: number): void {
    this.limit.set(limit);

    this.page.set(1);
  }

  setCurrentPage(page: number): void {
    this.page.set(page);
  }

  /**
   * Search through fields that make sense
   * to users looking at the table.
   */
  private matchesSearch(
    row: CloneResult,
    search: string,
  ): boolean {
    const searchableValues = [
      row.sourceProjectTitle,
      row.sourceProjectId,

      row.clientName,
      row.clientId,

      row.newProjectName,
      row.newProjectId,

      row.newProposalName,
      row.newProposalId,

      row.newInvoiceId,
    ];

    return searchableValues.some(
      (value) =>
        String(value ?? '')
          .toLowerCase()
          .includes(search),
    );
  }

  /**
   * Get one step from the row.
   */
  getStep(
    result: CloneResult,
    stepName: string,
  ): CloneStep | undefined {
    return result.steps?.find(
      (step) =>
        step.step === stepName,
    );
  }

  isStepSuccessful(
    result: CloneResult,
    stepName: string,
  ): boolean {
    return (
      result.steps?.some(
        (step) =>
          step.step === stepName &&
          step.status?.toLowerCase() ===
            'success',
      ) ?? false
    );
  }

  getStepLabel(step: string): string {
    return String(step ?? '')
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(
        /\b\w/g,
        (char) => char.toUpperCase(),
      );
  }

  getStatusClass(
    status?: string,
  ): string {
    switch (
      (status ?? '').toLowerCase()
    ) {
      case 'success':
        return (
          'status-pill ' +
          'status-pill--success'
        );

      case 'failed':
        return (
          'status-pill ' +
          'status-pill--failed'
        );

      case 'errored':
        return (
          'status-pill ' +
          'status-pill--errored'
        );

      case 'pending':
        return (
          'status-pill ' +
          'status-pill--pending'
        );

      default:
        return 'status-pill';
    }
  }

  private normalizeJobStatus(
    status: any,
  ): string {
    const value = String(
      status ?? '',
    ).toLowerCase();

    if (
      value === 'completed' ||
      value === 'success'
    ) {
      return 'success';
    }

    if (
      value === 'failed' ||
      value === 'fail'
    ) {
      return 'failed';
    }

    if (
      value === 'errored' ||
      value === 'error'
    ) {
      return 'errored';
    }

    return 'pending';
  }

  private normalizeStepStatus(
    status: any,
  ): string {
    const value = String(
      status ?? '',
    ).toLowerCase();

    if (
      value === 'success' ||
      value === 'completed' ||
      value === 'done'
    ) {
      return 'success';
    }

    if (
      value === 'failed' ||
      value === 'fail'
    ) {
      return 'failed';
    }

    if (
      value === 'errored' ||
      value === 'error'
    ) {
      return 'errored';
    }

    return 'pending';
  }
}